import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Eye, Loader2, Search, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  ['email', 'Email'],
  ['sms', 'SMS'],
  ['portal_message', 'Portal message'],
  ['document_request', 'Document request'],
  ['proposal_note', 'Proposal note'],
  ['stage_explanation', 'Stage explanation'],
  ['client_reminder', 'Client reminder'],
  ['post_settlement_check_in', 'Post-settlement check-in'],
];

export default function ClientPortalTemplates() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editing, setEditing] = useState(null);
  const [previewVars, setPreviewVars] = useState('{"client_name":"Sarah","broker_name":"Emmanuel","missing_doc":"payslips"}');
  const [preview, setPreview] = useState(null);

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/templates${filterType ? `?type=${filterType}` : ''}`, { headers: auth });
    if (r.ok) setTemplates(await r.json());
  };

  useEffect(() => { (async () => { try { await reload(); } finally { setLoading(false); } })(); /* eslint-disable-next-line */ }, [filterType]);

  const newDraft = () => setEditing({ name: '', type: 'email', subject: '', body: '' });

  const save = async () => {
    const isUpdate = !!editing.id;
    const url = isUpdate ? `${backendUrl}/api/admin/templates/${editing.id}` : `${backendUrl}/api/admin/templates`;
    const method = isUpdate ? 'PATCH' : 'POST';
    const res = await fetch(url, { method, headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(editing) });
    if (res.ok) { setEditing(null); reload(); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await fetch(`${backendUrl}/api/admin/templates/${t.id}`, { method: 'DELETE', headers: auth });
    reload();
  };

  const renderPreview = async () => {
    if (!editing?.id) { alert('Save the template first to preview.'); return; }
    let vars = {};
    try { vars = JSON.parse(previewVars); } catch (_) { alert('Invalid JSON for preview vars'); return; }
    const r = await fetch(`${backendUrl}/api/admin/templates/${editing.id}/render`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: vars }),
    });
    if (r.ok) setPreview(await r.json());
  };

  const filtered = templates.filter((t) =>
    !search.trim() || [t.name, t.subject, t.body].filter(Boolean).some((s) => s.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-900"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-2xl font-semibold text-slate-900">Templates</h1>
          <span className="ml-auto text-sm text-slate-500">{templates.length} total</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          <aside className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates"
                  data-testid="tpl-search"
                  className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-slate-200 bg-slate-50 text-sm"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="text-xs px-2 py-1.5 rounded ring-1 ring-slate-200 bg-white">
                  <option value="">All types</option>
                  {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button onClick={newDraft} data-testid="new-template" className="ml-auto inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline">
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
              </div>
            </div>
            {loading ? <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
              <ul className="divide-y divide-slate-100 max-h-[68vh] overflow-y-auto">
                {filtered.length === 0 && (
                  <li className="p-6 text-center text-slate-500 text-sm">
                    <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    No templates yet.
                  </li>
                )}
                {filtered.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => { setEditing(t); setPreview(null); }}
                      data-testid={`tpl-${t.id}`}
                      className={`w-full text-left p-3 hover:bg-slate-50 ${editing?.id === t.id ? 'bg-emerald-50/40' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-slate-800 truncate">{t.name}</div>
                        <span className="text-[10px] uppercase text-slate-500">{t.type.replace(/_/g, ' ')}</span>
                      </div>
                      {t.subject && <div className="text-xs text-slate-500 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <main className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 min-h-[60vh]">
            {!editing ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500 text-sm">
                <FileText className="w-10 h-10 text-slate-300 mb-2" />
                Select a template to edit, or click New.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="font-semibold text-slate-900">{editing.id ? 'Edit template' : 'New template'}</h2>
                  <div className="flex items-center gap-2">
                    {editing.id && (
                      <button onClick={() => remove(editing)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={save} data-testid="tpl-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium">
                      <Save className="w-3.5 h-3.5" /> Save
                    </button>
                  </div>
                </div>

                <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                <label className="block text-sm">
                  <span className="block text-slate-600 mb-1 text-xs uppercase tracking-wide">Type</span>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="tpl-type" className="w-full px-2.5 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm">
                    {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
                {(editing.type === 'email') && (
                  <Field label="Subject" value={editing.subject || ''} onChange={(v) => setEditing({ ...editing, subject: v })} />
                )}
                <Field label="Body (use {variable_name} for placeholders)" textarea value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testId="tpl-body" />

                <div className="border-t border-slate-100 pt-3">
                  <label className="block text-sm">
                    <span className="block text-slate-600 mb-1 text-xs uppercase tracking-wide flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> Preview variables (JSON)
                    </span>
                    <textarea
                      rows={3}
                      value={previewVars}
                      onChange={(e) => setPreviewVars(e.target.value)}
                      data-testid="tpl-preview-vars"
                      className="w-full px-2.5 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm font-mono"
                    />
                  </label>
                  <button onClick={renderPreview} className="mt-2 px-3 py-1.5 rounded ring-1 ring-slate-200 hover:bg-slate-50 text-sm">Render preview</button>

                  {preview && (
                    <div className="mt-3 rounded-lg ring-1 ring-emerald-200 bg-emerald-50/40 p-3 text-sm">
                      {preview.subject && <div className="font-semibold mb-1">Subject: {preview.subject}</div>}
                      <pre className="whitespace-pre-wrap font-sans">{preview.body}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testId }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-600 mb-1 text-xs uppercase tracking-wide">{label}</span>
      {textarea ? (
        <textarea
          rows={8}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full px-2.5 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm font-mono"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full px-2.5 py-1.5 rounded ring-1 ring-slate-200 bg-white text-sm"
        />
      )}
    </label>
  );
}
